import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  CREATE_ENTITY_CONFIG_URL,
  CREATE_ENTITY_TYPE_URL,
  CREATE_PROJECT_BINDINGS,
  GET_COMPANIES,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
  GET_ENTITY_TYPE_CATEGORIES_URL,
  GET_PROJECT_BINDINGS,
  REMOVE_PROJECT_BINDING_URL,
  UPDATE_ENTITY_TYPE_URL,
  VIEW_ENTITY_TYPE_URL,
  get_request_config
} from '@/utils/system_constants/constant.js';

export default {
  name: 'EntityConfigs',
  mixins: [alert_mixin],
  data: () => ({
    show_create_modal: false,
    show_edit_modal: false,
    show_assign_modal: false,
    creating_entity_type: false,
    updating_entity_type: false,
    assigning_project: false,
    inventory_loading: false,
    entity_type_categories: [],
    companies: [],
    companies_loading: false,
    assign_project_id: '',
    assign_entity: null,
    assign_project_ids: [],
    assign_entity_config_id: null,
    assign_bindings_loading: false,
    project_binding_by_project_id: {},
    project_toggle_loading_by_project_id: {},
    create_form: {
      entity_type_category: '',
      entity_type_name: '',
      entity_type_description: ''
    },
    edit_form: {
      entity_type_id: null,
      entity_type_name: '',
      entity_type_description: ''
    },
    inventory: [],
    total_entities_count: 0,
    current_page: 1,
    backend_page_size: 100,
    backend_total_pages: 0,
    current_backend_page: 1,
    frontend_page_size: 10,
    last_loaded_backend_page: 1,
    status_filter: 'all',
    sort_option: 'updated_desc',
    project_binding_entity_config_ids: null,
    project_binding_by_entity_config_id: null
  }),
  mounted() {
    this.fetch_entity_type_categories();
    this.fetch_entity_types();
  },
  computed: {
    metrics() {
      const total_entities = this.total_entities_count;
      const pending_review = this.inventory.filter((item) => item?.status !== 'Active').length;

      const result = [
        {
          label: 'Total Entities',
          value: String(total_entities),
          tone: 'primary',
          icon: 'bi-diagram-3'
        },
        {
          label: 'Pending Review',
          value: String(pending_review),
          tone: 'warning',
          icon: 'bi-hourglass-split'
        }
      ];

      return result;
    },
    selected_projects_count() {
      return Array.isArray(this.assign_project_ids) ? this.assign_project_ids.length : 0;
    },
    project_id_filter() {
      const raw = this.$route?.query?.project_id;
      const val = raw == null ? null : Number(raw);
      return Number.isFinite(val) ? val : null;
    },
    is_project_scoped() {
      return this.project_id_filter != null;
    },
    filtered_inventory() {
      return this.inventory;
    },
    pages_per_backend_page() {
      return Math.max(1, Math.floor(this.backend_page_size / this.frontend_page_size));
    },
    total_pages() {
      return Math.ceil(this.total_entities_count / this.frontend_page_size);
    },
    current_backend_batch_count() {
      return this.inventory.length;
    },
    current_chunk_page() {
      return ((this.current_page - 1) % this.pages_per_backend_page) + 1;
    },
    current_chunk_total_pages() {
      return Math.max(1, Math.ceil(this.current_backend_batch_count / this.frontend_page_size));
    },
    current_page_start() {
      if (!this.current_backend_batch_count) return 0;
      return ((this.current_chunk_page - 1) * this.frontend_page_size) + 1;
    },
    current_page_end() {
      if (!this.current_backend_batch_count) return 0;
      return Math.min(this.current_chunk_page * this.frontend_page_size, this.current_backend_batch_count);
    },
    visible_page_numbers() {
      return Array.from(
        { length: this.current_chunk_total_pages },
        (_, index) => index + 1
      );
    },
    paginated_inventory() {
      const localPageIndex = this.current_chunk_page - 1;
      const start = localPageIndex * this.frontend_page_size;
      const end = start + this.frontend_page_size;
      return this.filtered_inventory.slice(start, end);
    }
  },
  watch: {
    project_id_filter() {
      this.current_page = 1;
      this.fetch_entity_types(1);
    },
    status_filter() {
      this.current_page = 1;
      this.fetch_entity_types(1);
    },
    sort_option() {
      this.current_page = 1;
      this.fetch_entity_types();
    },
    paginated_inventory: {
      handler(items) {
        this.enrich_visible_inventory_items(items);
      },
      deep: false
    }
  },
  methods: {
    parse_response_data(payload) {
      let result = payload;

      if (typeof payload === 'string') {
        const trimmedPayload = payload.trim();
        const htmlPrefixPattern = /^<(?:!doctype|html)\b/i;

        if (!trimmedPayload) {
          result = {};
        } else if (htmlPrefixPattern.test(trimmedPayload)) {
          result = {
            status: 'error',
            message: 'The server returned HTML instead of JSON.'
          };
        } else {
          try {
            result = JSON.parse(trimmedPayload);
          } catch {
            result = {
              status: 'error',
              message: 'Invalid JSON response received from the server.',
            };
          }
        }
      }

      return result;
    },

    format_relative_date(dateString) {
      if (!dateString) return 'Recently updated';

      const createdDate = new Date(dateString);
      if (Number.isNaN(createdDate.getTime())) return 'Recently updated';

      const now = new Date();
      const diffMs = now - createdDate;
      const diffHours = Math.max(1, Math.floor(diffMs / (1000 * 60 * 60)));
      const diffDays = Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)));

      if (diffHours < 24) {
        return `Last updated ${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      }

      return `Last updated ${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    },

    map_entity_type_to_inventory_item(entityType, index = 0) {
      const iconOptions = ['bi-diagram-3', 'bi-shop', 'bi-briefcase-fill', 'bi-bank'];
      const toneOptions = ['primary', 'slate', 'peach'];
      const isActive = entityType.is_active !== false;
      const createdAt = entityType.date_created || entityType.date_updated || null;
      const updatedAt = entityType.date_updated || entityType.date_created || null;

      const result = {
        id: entityType.id,
        project_id: entityType.project_id || entityType.project?.id || null,
        name: entityType.entity_type_name || `Entity Type ${entityType.id}`,
        description: entityType.entity_type_description || '',
        updated: this.format_relative_date(updatedAt),
        icon: iconOptions[index % iconOptions.length],
        tone: toneOptions[index % toneOptions.length],
        status: isActive ? 'Active' : 'Inactive',
        status_class: isActive
          ? 'entity-status-active'
          : 'entity-status-review',
        version: `v${entityType.id}.0.0`,
        bindings: '0 Forms, 0 Workflows',
        binding_summary: [
          { key: 'forms', icon: 'bi-file-earmark-text', active: false },
          { key: 'workflows', icon: 'bi-diagram-3', active: false }
        ],
        entity_config_id: null,
        entity_config_project_id: null,
        forms_count: 0,
        workflows_count: 0,
        created_at: createdAt,
        updated_at: updatedAt
      };

      return result;
    },

    sort_inventory_items(items, sortOption = 'updated_desc') {
      return items.slice().sort((a, b) => {
        const aUpdated = new Date(a?.updated_at || a?.created_at || 0).getTime();
        const bUpdated = new Date(b?.updated_at || b?.created_at || 0).getTime();
        const aName = String(a?.name || '').toLowerCase();
        const bName = String(b?.name || '').toLowerCase();

        if (sortOption === 'name_asc') {
          return aName.localeCompare(bName) || ((Number(a?.id) || 0) - (Number(b?.id) || 0));
        }

        if (sortOption === 'name_desc') {
          return bName.localeCompare(aName) || ((Number(b?.id) || 0) - (Number(a?.id) || 0));
        }

        if (sortOption === 'updated_asc') {
          return aUpdated - bUpdated || ((Number(a?.id) || 0) - (Number(b?.id) || 0));
        }

        return bUpdated - aUpdated || ((Number(b?.id) || 0) - (Number(a?.id) || 0));
      });
    },

    fetch_entity_type_categories() {
      const promise = axios
        .get(GET_ENTITY_TYPE_CATEGORIES_URL, get_request_config())
        .then((response) => {
          const response_data = this.parse_response_data(response.data);
          this.entity_type_categories = response_data.data || [];
          const result = this.entity_type_categories;
          return result;
        })
        .catch(() => {
          this.entity_type_categories = [];
          const result = this.entity_type_categories;
          return result;
        });

      return promise;
    },

    get_backend_page_for_frontend_page(frontendPage) {
      return Math.ceil(frontendPage / this.pages_per_backend_page);
    },

    get_base_entity_type_request_params(frontendPage = this.current_page) {
      return {
        page: this.get_backend_page_for_frontend_page(frontendPage),
        page_size: this.backend_page_size,
        status_filter: this.status_filter,
        ordering: this.sort_option
      };
    },

    get_project_scoped_entity_type_params() {
      return this.project_id_filter == null
        ? {}
        : { project_id: this.project_id_filter };
    },

    build_entity_type_request_params(frontendPage = this.current_page) {
      return Object.assign(
        this.get_base_entity_type_request_params(frontendPage),
        this.get_project_scoped_entity_type_params()
      );
    },

    get_fallback_entity_type_request_config() {
      return get_request_config(
        this.project_id_filter != null
          ? { params: { project_id: this.project_id_filter } }
          : {}
      );
    },

    async request_entity_types(frontendPage) {
      try {
        return await axios.get(
          VIEW_ENTITY_TYPE_URL,
          get_request_config({
            params: this.build_entity_type_request_params(frontendPage)
          })
        );
      } catch (error) {
        return axios.get(
          VIEW_ENTITY_TYPE_URL,
          this.get_fallback_entity_type_request_config()
        );
      }
    },

    fetch_entity_types(frontendPage = this.current_page) {
      const promise = Promise.resolve()
        .then(async () => {
          this.inventory_loading = true;
          this.current_page = frontendPage;
          // Prevent flash of "all entities" when project-scoped.
          this.inventory = [];
          this.project_binding_by_entity_config_id = null;
          this.project_binding_entity_config_ids = null;
          if (this.project_id_filter != null) {
            const scoped = await this.fetch_project_bindings_map(this.project_id_filter);
            this.project_binding_entity_config_ids = scoped.ids;
            this.project_binding_by_entity_config_id = scoped.by_entity_config_id;
          }

          const response = await this.request_entity_types(frontendPage);
          const response_data = this.parse_response_data(response.data);
          const responseItems = Array.isArray(response_data.data) ? response_data.data : [];
          const backendCount = Number(response_data.count || responseItems.length || 0);
          this.total_entities_count = backendCount;
          this.last_loaded_backend_page = Number(response_data.page || 1);
          this.current_backend_page = this.last_loaded_backend_page;
          this.backend_total_pages = Number(response_data.total_pages || 0);
          const entityTypes = responseItems.map((item, index) =>
            this.map_entity_type_to_inventory_item(item, index)
          );
          this.inventory = entityTypes;

          if (!response_data.page) {
            const fallbackBackendPage = this.get_backend_page_for_frontend_page(this.current_page);
            const start = (fallbackBackendPage - 1) * this.backend_page_size;
            const end = start + this.backend_page_size;
            this.inventory = entityTypes.slice(start, end);
            this.last_loaded_backend_page = fallbackBackendPage;
            this.current_backend_page = fallbackBackendPage;
            this.backend_total_pages = Math.max(1, Math.ceil(entityTypes.length / this.backend_page_size));
          }

          await this.enrich_visible_inventory_items(this.paginated_inventory);

          const result = this.inventory;
          return result;
        })
        .catch(() => {
          this.inventory = [];
          this.total_entities_count = 0;
          this.backend_total_pages = 0;
          this.current_backend_page = 1;
          const result = this.inventory;
          return result;
        })
        .finally(() => {
          this.inventory_loading = false;
        });

      return promise;
    },

    fetch_project_bindings_map(projectId) {
      const result = axios
        .get(
          GET_PROJECT_BINDINGS,
          get_request_config({
            params: { project_id: projectId }
          })
        )
        .then((response) => {
          const parsed = this.parse_response_data(response.data);
          const items = Array.isArray(parsed?.data) ? parsed.data : [];
          const active = items.filter((binding) => binding?.is_active !== false);

          const by_entity_config_id = new Map();
          for (const binding of active) {
            const entity_config_id = Number(binding?.entity_config);
            const binding_id = Number(binding?.id);
            if (!Number.isFinite(entity_config_id) || !Number.isFinite(binding_id)) continue;
            by_entity_config_id.set(entity_config_id, binding_id);
          }

          const ids = new Set(by_entity_config_id.keys());
          return { ids, by_entity_config_id };
        })
        .catch(() => ({ ids: new Set(), by_entity_config_id: new Map() }));

      return result;
    },

    open_create_modal() {
      this.show_create_modal = true;
    },

    open_edit_modal(item) {
      this.edit_form = {
        entity_type_id: item?.id || null,
        entity_type_name: item?.name || '',
        entity_type_description: item?.description || ''
      };
      this.show_edit_modal = true;
    },

    async fetch_projects() {
      this.companies_loading = true;
      try {
        const response = await axios.get(
          GET_COMPANIES,
          get_request_config()
        );
        const response_data = this.parse_response_data(response.data);
        this.companies = response_data.data || response_data.projects || [];
      } catch (error) {
        this.companies = [];
        this.show_error('Failed to load projects.');
      } finally {
        this.companies_loading = false;
      }
    },

    project_display_name(project) {
      return project?.name || project?.project_name || project?.project?.name;
    },

    project_short_label(project) {
      const name = this.project_display_name(project) || '';
      const parts = name.split(' ');
      return (parts[0]?.[0] + (parts[1]?.[0] || '')).toUpperCase() || 'PR';
    },

    fetch_project_bindings(projectId) {
      if (!projectId) return Promise.resolve([]);
      return axios
        .get(
          GET_PROJECT_BINDINGS,
          get_request_config({
            params: { project_id: projectId }
          })
        )
        .then((response) => {
          const parsed = this.parse_response_data(response.data);
          const items = Array.isArray(parsed?.data) ? parsed.data : [];
          return items;
        })
        .catch(() => []);
    },

    async load_assign_project_bindings() {
      const entityTypeId = this.assign_entity?.id;
      if (!entityTypeId) return;

      this.assign_bindings_loading = true;
      this.assign_project_ids = [];
      this.project_binding_by_project_id = {};
      this.assign_entity_config_id = null;

      try {
        const config_data = await this.fetch_full_config_by_entity_type(entityTypeId);
        const entityConfigId = config_data?.entity_config?.id;
        if (!entityConfigId) return;
        this.assign_entity_config_id = Number(entityConfigId);

        const projects = Array.isArray(this.companies) ? this.companies : [];
        const checks = await Promise.all(
          projects.map(async (project) => {
            try {
              const bindings = await this.fetch_project_bindings(project.id);
              const match = bindings.find((b) =>
                String(b?.entity_config) === String(entityConfigId) && b?.is_active !== false
              );

              return {
                projectId: project.id,
                bindingId: match?.id || null
              };
            } catch {
              return { projectId: project.id, bindingId: null };
            }
          })
        );

        const assigned = [];
        const map = {};
        for (const entry of checks) {
          if (entry?.bindingId) {
            assigned.push(String(entry.projectId));
            map[String(entry.projectId)] = Number(entry.bindingId);
          }
        }

        this.assign_project_ids = assigned;
        this.project_binding_by_project_id = map;
      } catch (error) {
        this.show_error('Failed to load assigned projects');
      } finally {
        this.assign_bindings_loading = false;
      }
    },

    is_project_selected(projectId) {
      const ids = Array.isArray(this.assign_project_ids) ? this.assign_project_ids : [];
      return ids.includes(String(projectId));
    },

    is_project_toggle_loading(projectId) {
      return Boolean(this.project_toggle_loading_by_project_id?.[String(projectId)]);
    },

    async open_assign_modal(item) {
      this.assign_entity = item;
      this.assign_project_id = item.project_id ? String(item.project_id) : '';
      this.show_assign_modal = true;

      if (!this.companies.length) {
        await this.fetch_projects();
      }

      await this.load_assign_project_bindings();
    },

    close_assign_modal(modal_id = 'assign_project') {
      this.close_modal(modal_id);
      this.show_assign_modal = false;
      this.assign_entity = null;
      this.assign_project_ids = [];
      this.assign_entity_config_id = null;
      this.assign_bindings_loading = false;
      this.project_binding_by_project_id = {};
      this.project_toggle_loading_by_project_id = {};
    },

    async set_project_assignment(project, shouldAssign) {
      const project_id = Number(project?.id);
      if (!Number.isFinite(project_id) || project_id <= 0) return;

      if (!this.assign_entity?.id) return;

      if (!this.assign_entity_config_id) {
        await this.load_assign_project_bindings();
        if (!this.assign_entity_config_id) return;
      }

      const project_id_key = String(project_id);
      this.project_toggle_loading_by_project_id = {
        ...(this.project_toggle_loading_by_project_id || {}),
        [project_id_key]: true
      };

      try {
        const should_assign = Boolean(shouldAssign);
        const is_assigned = this.is_project_selected(project_id);
        if (should_assign === is_assigned) return;

        if (should_assign) {
          const res = await axios.post(
            CREATE_PROJECT_BINDINGS,
            {
              entity_config_id: this.assign_entity_config_id,
              project_id: project_id_key
            },
            get_request_config()
          );

          const data = this.parse_response_data(res.data);
          if (data?.status && data?.status !== 'success') {
            throw new Error(data?.message || 'Assignment failed');
          }

          // Refresh binding id for this project so we can unassign later.
          const bindings = await this.fetch_project_bindings(project_id);
          const match = bindings.find((b) =>
            String(b?.entity_config) === String(this.assign_entity_config_id) && b?.is_active !== false
          );
          if (match?.id) {
            this.project_binding_by_project_id = {
              ...(this.project_binding_by_project_id || {}),
              [project_id_key]: Number(match.id)
            };
          }

          const next_ids = new Set(Array.isArray(this.assign_project_ids) ? this.assign_project_ids : []);
          next_ids.add(project_id_key);
          this.assign_project_ids = Array.from(next_ids);
        } else {
          const binding_id = Reflect.get(this.project_binding_by_project_id ?? {}, project_id_key);
          if (binding_id) {
            const res = await axios.post(
              REMOVE_PROJECT_BINDING_URL,
              { binding_id: Number(binding_id) },
              get_request_config()
            );
            const data = this.parse_response_data(res.data);
            if (data?.status && data?.status !== 'success') {
              throw new Error(data?.message || 'Unassign failed');
            }
          }

          this.assign_project_ids = (Array.isArray(this.assign_project_ids) ? this.assign_project_ids : [])
            .filter((id) => String(id) !== project_id_key);

          // Avoid `delete` with computed keys (Codacy: object injection sink).
          this.project_binding_by_project_id = Object.fromEntries(
            Object.entries(this.project_binding_by_project_id || {}).filter(([entry_key]) => entry_key !== project_id_key)
          );
        }
      } catch (error) {
        this.show_error(error?.message || 'Failed to update assignment');
        await this.load_assign_project_bindings();
      } finally {
        this.project_toggle_loading_by_project_id = {
          ...(this.project_toggle_loading_by_project_id || {}),
          [project_id_key]: false
        };
      }
    },

    on_project_toggle(project, evt) {
      const checked = Boolean(evt?.target?.checked);
      return this.set_project_assignment(project, checked);
    },

    async fetch_full_config_by_entity_type(entity_type_id) {
      const response = await axios.get(
        GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
        get_request_config({
          params: {
            entity_type_id
          }
        })
      );

      const response_data = this.parse_response_data(response.data);
      return response_data.data || null;
    },

    should_enrich_inventory_item(item) {
      if (!item?.id) return false;

      return !(
        item.forms_count > 0 ||
        item.workflows_count > 0 ||
        item.entity_config_id
      );
    },

    build_enriched_inventory_item(item, config_data) {
      const form_bindings = config_data?.form_bindings || [];
      const workflow_bindings = config_data?.workflow_bindings || [];
      const config_project_id =
        config_data?.entity_config?.project_id ||
        config_data?.entity_type?.project_id ||
        config_data?.entity_type?.project?.id ||
        null;

      return {
        ...item,
        entity_config_id: config_data?.entity_config?.id || null,
        entity_config_project_id: config_project_id,
        forms_count: form_bindings.length,
        workflows_count: workflow_bindings.length,
        bindings: `${form_bindings.length} Forms, ${workflow_bindings.length} Workflows`,
        binding_summary: [
          {
            key: 'forms',
            icon: 'bi-file-earmark-text',
            active: form_bindings.length > 0
          },
          {
            key: 'workflows',
            icon: 'bi-diagram-3',
            active: workflow_bindings.length > 0
          }
        ]
      };
    },

    async enrich_inventory_item(item) {
      if (!this.should_enrich_inventory_item(item)) {
        return item;
      }

      try {
        const config_data = await this.fetch_full_config_by_entity_type(item.id);
        return this.build_enriched_inventory_item(item, config_data);
      } catch (error) {
        return item;
      }
    },

    is_item_visible_in_scope(item, updateMap) {
      if (this.project_id_filter == null) return true;

      const binding_ids = this.project_binding_entity_config_ids;
      if (!binding_ids?.size) return false;

      const resolvedItem = updateMap.get(item.id) || item;
      return binding_ids.has(Number(resolvedItem.entity_config_id));
    },

    merge_inventory_updates(updates) {
      const updateMap = new Map(updates.map((item) => [item.id, item]));

      return this.inventory
        .map((item) => updateMap.get(item.id) || item)
        .filter((entry) => this.is_item_visible_in_scope(entry, updateMap));
    },

    has_inventory_changed(nextInventory) {
      if (nextInventory.length !== this.inventory.length) {
        return true;
      }

      const currentIterator = this.inventory.values();
      for (const item of nextInventory) {
        if (item !== currentIterator.next().value) {
          return true;
        }
      }

      return false;
    },

    async enrich_visible_inventory_items(items) {
      const visibleItems = Array.isArray(items) ? items : [];
      const updates = await Promise.all(
        visibleItems.map((item) => this.enrich_inventory_item(item))
      );

      const nextInventory = this.merge_inventory_updates(updates);

      if (this.has_inventory_changed(nextInventory)) {
        this.inventory = nextInventory;
      }
    },

    async unassign_project_from_entity_type(item) {
      const project_id = this.project_id_filter;
      if (project_id == null) return;

      const entity_config_id = Number(item?.entity_config_id);
      if (!Number.isFinite(entity_config_id)) {
        this.show_error('This entity config has no binding to remove.');
        return;
      }

      const binding_id = this.project_binding_by_entity_config_id?.get(entity_config_id);
      if (!binding_id) {
        this.show_error('Binding id not found for this entity config.');
        return;
      }

      const loader = await this.show_loader('Unassigning project...');
      try {
        const res = await axios.post(
          REMOVE_PROJECT_BINDING_URL,
          { binding_id: Number(binding_id) },
          get_request_config()
        );
        const data = this.parse_response_data(res.data);
        if (data?.status !== 'success') {
          throw new Error(data?.message || 'Unassign failed');
        }

        // Project-scoped view shows only assigned, so remove immediately.
        this.inventory = this.inventory.filter(
          (entry) => Number(entry.entity_config_id) !== entity_config_id
        );

        const nextIds = new Set(this.project_binding_entity_config_ids || []);
        nextIds.delete(entity_config_id);
        this.project_binding_entity_config_ids = nextIds;

        const nextMap = new Map(this.project_binding_by_entity_config_id || []);
        nextMap.delete(entity_config_id);
        this.project_binding_by_entity_config_id = nextMap;

        this.show_success(data?.message || 'Unassigned successfully.');
      } catch (error) {
        this.show_error(error?.message || 'Failed to unassign project.');
        await this.fetch_entity_types();
      } finally {
        await this.close_loader(loader);
      }
    },

    async open_entity_config(item) {
      try {
        let config_data = null;

        try {
          config_data = await this.fetch_full_config_by_entity_type(item.id);
        } catch (error) {
          if (error?.response?.status === 404) {
            const create_response = await axios.post(
              CREATE_ENTITY_CONFIG_URL,
              {
                entity_type_id: item.id
              },
              get_request_config()
            );
            const created_config = this.parse_response_data(create_response.data);
            if (!created_config?.data?.id) {
              throw new Error('Entity config was not created successfully.');
            }
            config_data = await this.fetch_full_config_by_entity_type(item.id);
          } else {
            throw error;
          }
        }

        this.inventory = this.inventory.map((entry) =>
          entry.id === item.id
            ? {
              ...entry,
              entity_config_id: config_data?.entity_config?.id || entry.entity_config_id
            }
            : entry
        );

        this.$router.push({
          name: 'EntityConfigDetail',
          params: { entityTypeId: item.id },
          query: this.project_id_filter ? { project_id: String(this.project_id_filter) } : undefined
        });
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.show_error(response_data.message || error.message || 'Failed to open entity config.');
      }
    },

    // Assignment is now handled inline via the modal toggles.

    close_create_modal() {
      this.show_create_modal = false;
      this.create_form = {
        entity_type_category: '',
        entity_type_name: '',
        entity_type_description: ''
      };
    },

    close_edit_modal() {
      this.show_edit_modal = false;
      this.edit_form = {
        entity_type_id: null,
        entity_type_name: '',
        entity_type_description: ''
      };
    },

    async create_entity_type() {
      if (!this.create_form.entity_type_name.trim()) {
        this.show_error('Entity name is required.');
        return;
      }

      this.creating_entity_type = true;
      const loader = await this.show_loader('Creating entity type...');

      try {
        const payload = {
          entity_type_name: this.create_form.entity_type_name.trim(),
          entity_type_description: this.create_form.entity_type_description.trim()
        };

        if (this.create_form.entity_type_category) {
          payload.entity_type_category = Number(
            this.create_form.entity_type_category
          );
        }

        const response = await axios.post(
          CREATE_ENTITY_TYPE_URL,
          payload,
          get_request_config()
        );
        const response_data = this.parse_response_data(response.data);
        if (response_data.data) {
          await this.fetch_entity_types(1);
        } else {
          await this.fetch_entity_types();
        }
        this.close_create_modal();
        this.show_success(response_data.message || 'Entity type created successfully.');
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.show_error(
          response_data.message ||
          response_data.errors ||
          'Failed to create entity type.'
        );
      } finally {
        await this.close_loader(loader);
        this.creating_entity_type = false;
      }
    },

    async update_entity_type() {
      if (!this.edit_form.entity_type_id) {
        this.show_error('Entity type not found.');
        return;
      }

      if (!this.edit_form.entity_type_name.trim()) {
        this.show_error('Entity name is required.');
        return;
      }

      this.updating_entity_type = true;
      const loader = await this.show_loader('Updating entity type...');

      try {
        const payload = {
          entity_type_id: Number(this.edit_form.entity_type_id),
          entity_type_name: this.edit_form.entity_type_name.trim(),
          entity_type_description: this.edit_form.entity_type_description.trim()
        };

        const response = await axios.post(
          UPDATE_ENTITY_TYPE_URL,
          payload,
          get_request_config()
        );
        const response_data = this.parse_response_data(response.data);

        this.inventory = this.inventory.map((item) =>
          item.id === payload.entity_type_id
            ? {
              ...item,
              name: payload.entity_type_name,
              description: payload.entity_type_description
            }
            : item
        );

        this.close_edit_modal();
        await this.fetch_entity_types(this.current_page);
        this.show_success(response_data.message || 'Entity type updated successfully.');
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.show_error(
          response_data.message ||
          response_data.errors ||
          'Failed to update entity type.'
        );
      } finally {
        await this.close_loader(loader);
        this.updating_entity_type = false;
      }
    },

    async go_to_page(localPage) {
      if (localPage < 1 || localPage > this.current_chunk_total_pages) return;

      const absolutePage =
        ((this.current_backend_page - 1) * this.pages_per_backend_page) + localPage;

      if (absolutePage === this.current_page) return;

      const targetBackendPage = this.get_backend_page_for_frontend_page(absolutePage);
      if (targetBackendPage !== this.last_loaded_backend_page) {
        await this.fetch_entity_types(absolutePage);
        return;
      }

      this.current_page = absolutePage;
    }
  }
};
